import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom'; // Added Link for navigation
import axios from 'axios'; // Using axios for consistency and better error handling
import { FaUser, FaClock, FaLock, FaUnlock } from 'react-icons/fa'; // For icons in the cards
import API_CONFIG from '../config/api';
import '../pages/CategoryPage.css'; // Importing the CSS for CategoryPage
import NavigationBar from '../components/layout/NavigationBar';
const UserPage = () => {
    const [pages, setPages] = useState([]); // Renamed from userData to pages for consistency
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { username } = useParams();

    // Memoize the default cover image path, as used in CategoryPage
    const defaultCoverImage = useMemo(() => '/default-cover.jpg', []);

    // Extract content preview function (copied from CategoryPage)
    const getContentPreview = useCallback((content) => {
        if (!content || content.length === 0) return '';

        let textSource = '';
        for (let i = 0; i < content.length; i++) {
            const item = content[i];
            if (item.content && typeof item.content === 'string' && item.content.trim().length > 0) {
                textSource = item.content;
                break;
            }
        }

        if (!textSource) {
            for (let i = 0; i < content.length; i++) {
                const item = content[i];
                if (item.body && typeof item.body === 'string' && item.body.trim().length > 0) {
                    textSource = item.body;
                    break;
                } else if (item.title && typeof item.title === 'string' && item.title.trim().length > 0) {
                    textSource = item.title;
                    break;
                }
            }
        }

        if (!textSource) return '';

        textSource = textSource.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

        const words = textSource.split(' ');
        const preview = words.slice(0, 15).join(' ');

        return words.length > 15 ? preview + '...' : preview;
    }, []);

    // Format date function (copied from CategoryPage)
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';

        return new Date(dateString).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    }, []);

    // Memoized page card rendering function (copied from CategoryPage)
    const renderPageCard = useCallback((page) => {
        const contentPreview = getContentPreview(page.content);
        const publishDate = page.published_at || page.created_at || new Date().toISOString();
        const formattedDate = formatDate(publishDate);

        return (
            <div key={page.id} className="page-card">
                <Link to={`/${page.user?.username || 'user'}/${page.slug}`} className="card-link">
                    {page.is_paid ? (
                        <div className="access-tag premium"><FaLock /> SUBSCRIBED</div>
                    ) : (
                        <div className="access-tag free"><FaUnlock /> FREE VERSION</div>
                    )}

                    <div className="card-content">
                        <h3 className="card-title">{page.title || 'Untitled Page'}</h3>

                        <div className="card-secondary-image">
                            <img
                                src={defaultCoverImage} // Using the default cover image as in CategoryPage
                                alt="Content preview"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    console.error(`Failed to load image: ${e.target.src}`);
                                }}
                            />
                        </div>

                        <p className="card-description">{page.description || 'Photography content'}</p>

                        {contentPreview && (
                            <div className="content-preview">
                                <p>{contentPreview} <span className="read-more">Read more</span></p>
                            </div>
                        )}

                        <div className="card-footer">
                            <div className="creator-info">
                                <span className="creator-avatar">
                                    <FaUser />
                                </span>
                                <span className="creator-name">{page.user?.name || page.user?.username || 'Unknown'}</span>
                            </div>
                            <span className="publish-date">
                                <FaClock className="date-icon" /> {formattedDate}
                            </span>
                        </div>
                    </div>
                </Link>
            </div>
        );
    }, [getContentPreview, defaultCoverImage, formatDate]);

    // Data fetching effect
    useEffect(() => {
        let isMounted = true; // To prevent state updates on unmounted component

        const fetchUserPages = async () => {
            if (!username) {
                setError('No username specified.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');
                // Assuming the API endpoint for a user's pages is something like /users/{username}/pages
                // If your endpoint `/${username}` directly returns pages by that user, keep it.
                // Based on your original code: `userData[0].user.username` and `item.title`, it seems
                // the `/${username}` endpoint might return an array of pages.
                const response = await axios.get(`${API_CONFIG.API_URL}/${username}`);

                if (isMounted) {
                    // Assuming the API response is an array of page objects
                    setPages(response.data);
                }
            } catch (err) {
                if (isMounted) {
                    setError(`Failed to load pages for user "${username}".`);
                    console.error('Error fetching user data:', err);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUserPages();

        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, [username]); // Rerun when username changes

    // --- Loading, Error, and No Pages Found States (from CategoryPage) ---
    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading user pages...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Something went wrong</h2>
                <p>{error}</p>
                <Link to="/">Return to Home</Link>
            </div>
        );
    }

    // --- Main Content Display ---
    return (
        <div className="category-page-container"> {/* Reusing CategoryPage's main container class */}
            <NavigationBar /> {/* Reusing NavigationBar from CategoryPage */}
            <div className="category-header"> {/* Reusing CategoryPage's header class */}
                <h1>User Profile: {username}</h1>
                <p>Explore pages created by {username}.</p>
            </div>

            {pages.length > 0 ? (
                <div className="page-grid"> {/* Reusing CategoryPage's grid class */}
                    {pages.map(page => renderPageCard(page))}
                </div>
            ) : (
                <div className="no-pages-found"> {/* Reusing CategoryPage's no-pages-found class */}
                    <h2>No Pages Found</h2>
                    <p>There are no pages available for "{username}" yet.</p>
                    <Link to="/">Explore other categories</Link>
                </div>
            )}
        </div>
    );
};

export default UserPage;